/* global mainScript */
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { Sizes } from "../../styles";

const SearchBar = ({
	isSearchOpen,
	setIsSearchOpen,
	searchTerm,
	setSearchTerm,
	filterWidths,
}) => {
	const isDesktop = useMediaQuery({ query: "(min-width: 1024px)" });
	const inputRef = useRef(null);

	const toggleSearch = () => setIsSearchOpen((currentValue) => !currentValue);
	const focusInput = () => inputRef.current.focus();

	// Prevent form submission from reloading the page
	const handleSubmit = (e) => {
		e.preventDefault();
	};

	// Calculate the width of the search input
	// Desktop: width needs to be sum of all filters + gap
	// Mobile: width needs to be of one filter
	const inputWidth = isDesktop
		? filterWidths.reduce((a, b) => a + b, 0) + 8
		: filterWidths[0];

	return (
		<SearchContainer>
			<Icon
				aria-label="Open search input"
				className="icon"
				onClick={toggleSearch}
				tabIndex={0}
				role="button"
			>
				<img
					src={`${mainScript.assetPath}images/svg/icons/icon-search-black.svg`}
					alt="Open search input for media items"
				/>
			</Icon>
			<AnimatePresence>
				{isSearchOpen && (
					<Form role="search" onSubmit={handleSubmit}>
						<InputContainer>
							<InputLabel>Enter your search</InputLabel>
							<Input
								ref={inputRef}
								type="text"
								placeholder="Search"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								initial={{ width: 0, opacity: 0 }}
								animate={{ width: inputWidth, opacity: 1 }}
								exit={{ width: 0, opacity: 0 }}
								transition={{ width: { duration: 0.5 } }}
								onAnimationComplete={focusInput}
							/>
							<IconClose
								onClick={toggleSearch}
								aria-label="close search input"
								tabIndex={0}
								role="button"
								initial={{ opacity: 1 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0, scale: 0.5 }}
								transition={{
									opacity: { duration: 0.3 },
									scale: { duration: 0.3 },
								}}
							>
								<img
									src={`${mainScript.assetPath}images/svg/icons/icon-close.svg`}
									alt="Close"
								/>
							</IconClose>
						</InputContainer>
					</Form>
				)}
			</AnimatePresence>
		</SearchContainer>
	);
};

SearchBar.propTypes = {
	searchTerm: PropTypes.string,
	setSearchTerm: PropTypes.func,
	filterWidths: PropTypes.array,
	isSearchOpen: PropTypes.bool,
	setIsSearchOpen: PropTypes.func,
};

export default SearchBar;

const SearchContainer = styled.div`
	display: inline;
`;

const Icon = styled.div`
	width: 25.31px;
	height: 25.31px;
	cursor: pointer;

	> img {
		width: 100%;
		height: 100%;
	}
`;

const Form = styled.form`
	position: absolute;
	bottom: -7rem;
	left: 0;
	z-index: 11;
	width: 100%;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		bottom: 0;
	}
`;

const InputContainer = styled.div`
	display: flex;
	align-items: center;
	position: relative;
	width: 100%;
	justify-content: space-between;
	gap: 2.6rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		justify-content: unset;
	}
`;

const Input = styled(motion.input)`
	height: 5rem;
	padding: 0.8rem 1.4rem !important;
	border: 1px solid #d9d9d9;
	border-radius: 4px !important;
	margin-bottom: 0 !important;
	width: 100%;

	&::placeholder {
		font-size: 1.6rem;
		line-height: 2.7rem;
	}

	&:focus {
		color: #232323 !important;
	}

	&:focus-visible {
		outline-color: #d9d9d9;
	}
`;

const InputLabel = styled.label`
	position: absolute;
	top: -22px;
	visibility: hidden;
`;

const IconClose = styled(motion.div)`
	width: 18px;
	height: 18px;
	cursor: pointer;
	position: absolute;
	right: 37px;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		position: unset;
		right: unset;
	}

	> img {
		width: 100%;
		height: 100%;
	}
`;
